import {useTranslation} from "react-i18next";
import {Consts} from "../pages/consts";
import React, {useContext, useEffect, useState} from "react";
import useMmcService from "../../services/MmcService";
import InitContext from "../pages/InitContext";
import PatientGenes from "../pgx/PatientGenes";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import CreateNewGeneView from "../pgx/CreateNewGeneView";
import NewPgxTest from "../pgx/NewPgxTest";
import {getPatientName} from "../pages/utils";
import useDialogDrag from "../../hooks/useDialogDrag";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

const PatientPGx = ({
                      patient,
                      updatePatient,
                      showDialog,
                      onCloseDialog,
                    }) => {
  const {t} = useTranslation();
  const [newPgx, setNewPgx] = useState(null);
  const [patGenes, setPatGenes] = useState([]);
  const [createNewDialogOpened, setCreateNewDialogOpened] = useState(false);
  const [newGenesSelected, setNewGenesSelected] = useState(false);
  const [availableGeneNames, setAvailableGeneNames] = useState([]);
  const context = useContext(InitContext);
  const {clearError, error, loading, saveDbGeneTest, getDbGenes} = useMmcService();

  useEffect(() => {
    if (patGenes === undefined || patGenes === null || patGenes?.length === 0) {
      fetchDbGenes().then();
    }
  }, [patient?.id]);

  const fetchDbGenes = async () => {
    clearError();

    try {
      const genesInDb = await getDbGenes(context?.token, patient?.id);

      if(genesInDb){
        setPatGenes(genesInDb);
      }

      if (!availableGeneNames?.length) {
        setAvailableGeneNames(Consts.ALL_GENE_NAMES);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveNewGeneTest = async () => {
    clearError();

    saveDbGeneTest(context?.token, newPgx, patient?.id).then(() => {
      clearError();

      getDbGenes(context?.token, patient?.id).then((genesInDb) => {
        setPatGenes(genesInDb);
        setNewPgx(null);
        setCreateNewDialogOpened(false);
        setNewGenesSelected(false);
        setAvailableGeneNames(Consts.ALL_GENE_NAMES);
        updatePatient(genesInDb);
      });
    });
  };

  const handleSetPatGenes = (newGenes) => {
    setPatGenes(newGenes)
  }

  const handleInitNewTest = (newGenes) => {
    setNewPgx((prev) => ({...prev, genes: newGenes}));
  }

  const showGenesAfterStoringNewTest = (newGenes) => {
    setNewGenesSelected(false);
    setCreateNewDialogOpened(false)
    setNewPgx((prev) => ({...prev, genes: newGenes}));
    setPatGenes(newGenes);
  }

  const handleCreateNewTest = () => {
    setNewPgx(null);
    setCreateNewDialogOpened(true);
    setNewGenesSelected(false);
    setAvailableGeneNames(Consts.ALL_GENE_NAMES);
  }

  const handleCancel = () => {
    fetchDbGenes().then(() => {
      setNewPgx(null);
      setCreateNewDialogOpened(false);
      setNewGenesSelected(false);
      setAvailableGeneNames(Consts.ALL_GENE_NAMES);
    });
  }

  const errorMessage = error && <ErrorMessage message={ t(error?.includes(Consts.POST)
    ? "error_by_saving_pgx_test_data"
    : "error_by_getting_pgx_test_data") }/>;

  const spinner = loading && <Spinner/>;
  const disabledSeeNames = context?.role === Consts.STUDY_DIRECTOR || context?.role === "admin";
  const { dragStyle, isDragging, onDragHandleMouseDown } = useDialogDrag(showDialog);
  const dialogTitle = `${t("pharmacogenetic_test_results")}${context?.role !== Consts.PATIENT ? `: ${getPatientName(patient, disabledSeeNames)}` : ""}`;

  return (
    <div className="w3-modal" style={ {display: showDialog ? Consts.BLOCK : Consts.NONE} }>
      <div
        className={`w3-modal-content w3-card-4 w3-animate-zoom w3-theme-l3 dialog-wide disease-tree dialog-draggable ${isDragging ? "dialog-draggable--dragging" : ""}`}
        style={dragStyle}
      >
        <header className="dialog-drag-header" onMouseDown={onDragHandleMouseDown}>
          <strong className="dialog-drag-header__title">{dialogTitle}</strong>
          <button
            type="button"
            className="dialog-drag-close"
            onClick={onCloseDialog}
            aria-label={t("close", { defaultValue: "Close" })}
            title={t("close", { defaultValue: "Close" })}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>
        { (!createNewDialogOpened && !newGenesSelected && context?.role === Consts.HCP_PRESCRIBER && !newPgx) &&
          <div className="w3-container w3-right-align w3-padding-small">
            <button className="w3-btn w3-round-xlarge w3-theme-d5" onClick={ handleCreateNewTest }>
              { t("add_more_genes") }
            </button>
          </div>
        }
        { errorMessage }
        { spinner }
        <div className="w3-container" id="pgx-parent">
          { createNewDialogOpened &&
            <CreateNewGeneView newPgx={ newPgx } setNewPgx={ setNewPgx } availableGeneNames={ availableGeneNames }
                               setAvailableGeneNames={ setAvailableGeneNames } onHandleInitNewTest={ handleInitNewTest }
                               onHandleNextClick={ () => setCreateNewDialogOpened(false) }
                               onHandleCancel={ handleCancel }/>
          }
          { !createNewDialogOpened && newPgx !== null && // shows a view with rows of a new PGx (not yet saved, no ID)
            <>
              <NewPgxTest newPgx={ newPgx } setNewPgx={ setNewPgx } handleSetPatGenes={ showGenesAfterStoringNewTest }
                          patientId={  patient?.id } handleSaveNewGeneTest={ handleSaveNewGeneTest }
                          handleCancel={ handleCancel }/>
            </>
          }
          { !createNewDialogOpened && newPgx === null && // shows PGx with trash icons (saved, with ID)
            <div className="w3-container">
              <PatientGenes patGenes={ patGenes } setPatGenes={ handleSetPatGenes } patientId={ patient?.id } />
            </div>
          }
        </div>
      </div>
    </div>
  );
};

export default PatientPGx;
